import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  errorToString,
  FundReleaseType,
  FundingSchedule,
  getSolarMonitoringStatusLabel,
  IChecklistItem,
  isSolarMonitoringCheckComplete,
  LoanFundingPhaseShared,
  SolarMonitoringCheckKind,
  SolarMonitoringDetails,
  SolarMonitoringEvidence,
  SolarMonitoringProvider,
  StatusData,
  ChecklistOwner,
} from '@oneethos/shared'
import { toast } from 'react-toastify'
import api from '../api-client'
import { CheckboxIcon } from './checkbox-icon'
import { Spinner } from '@fluentui/react'
import { Modal } from './modal'

type ChecklistItemSolarMonitoringProps = {
  item: IChecklistItem
  project: StatusData
  onClick: () => void
  isExpanded: boolean
  onProjectChange?: (project: StatusData) => void
}

type WorkingAction = 'save' | 'simulate' | 'clear'

type ModalMessage = {
  text: string
  variant: 'muted' | 'success' | 'warning'
}

type InverterConnectivityModalProps = {
  checkKind: SolarMonitoringCheckKind
  canEdit: boolean
  canClear: boolean
  workingAction: WorkingAction | null
  selectedProvider: SolarMonitoringProvider
  availableProviders: SolarMonitoringProvider[]
  externalSystemId: string
  isLocalDevelopment: boolean
  message: ModalMessage | null
  onClose: () => void
  onProviderChange: (provider: SolarMonitoringProvider) => void
  onExternalSystemIdChange: (value: string) => void
  onSaveAndCheck: () => void
  onSimulate: () => void
  onClear: () => void
}

type SolarMonitoringFundingCalloutProps = {
  checkKind: SolarMonitoringCheckKind
}

type InverterModalStatusProps = {
  message: ModalMessage
}

const providerLabel = (provider?: SolarMonitoringProvider): string => (
  provider === SolarMonitoringProvider.SolarEdge ? 'SolarEdge' : 'Enphase'
)

export const isMonitoringGrantUnavailableError = (error: unknown): boolean => (
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  error.code === 'SOLAR_MONITORING_GRANT_UNAVAILABLE'
)

export const isMonitoringCheckCooldownError = (error: unknown): boolean => (
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  error.code === 'SOLAR_MONITORING_CHECK_COOLDOWN'
)

const SolarMonitoringFundingCallout = ({ checkKind }: SolarMonitoringFundingCalloutProps) => (
  <div className="solar-monitoring-checklist-callout mt-2">
    {checkKind === SolarMonitoringCheckKind.Production ? (
      <>
        <strong>Production verification</strong>
        <div className="small text-muted">
          Confirm three consecutive qualifying production days from the connected inverter
        </div>
      </>
    ) : (
      <>
        <strong>Connectivity verification</strong>
        <div className="small text-muted">Configure site monitoring to verify inverter connectivity</div>
      </>
    )}
  </div>
)

type ManageSiteDetailsButtonProps = {
  disabled: boolean
  onClick: () => void
}

const ManageSiteDetailsButton = ({
  disabled,
  onClick,
}: ManageSiteDetailsButtonProps) => (
  <button
    className="btn btn-tiny btn-primary"
    disabled={disabled}
    onClick={event => {
      event.stopPropagation()
      onClick()
    }}
    type="button"
  >
    Manage Site Details
  </button>
)

const InverterModalStatus = ({ message }: InverterModalStatusProps) => {
  const className = message.variant === 'success'
    ? 'text-success'
    : message.variant === 'warning'
      ? 'text-warning'
      : 'text-muted'

  return (
    <div className={`small mb-3 ${className}`}>
      {message.text}
    </div>
  )
}

const InverterConnectivityModal = ({
  checkKind,
  canEdit,
  canClear,
  workingAction,
  selectedProvider,
  availableProviders,
  externalSystemId,
  isLocalDevelopment,
  message,
  onClose,
  onProviderChange,
  onExternalSystemIdChange,
  onSaveAndCheck,
  onSimulate,
  onClear,
}: InverterConnectivityModalProps) => {
  const working = workingAction !== null
  const canSubmit = !working && Boolean(selectedProvider) && Boolean(externalSystemId.trim())
  const isProductionCheck = checkKind === SolarMonitoringCheckKind.Production

  return ReactDOM.createPortal(
    <Modal
      containerStyles={{ maxWidth: '480px' }}
      onClose={onClose}
      title={'Site Details'}
    >
      {/* <p className="text-muted">
        {isProductionCheck
          ? 'Confirm three consecutive qualifying production days from the connected inverter.'
          : 'Save inverter details and check connectivity.'}
      </p> */}
      <div className="mb-3">
        <label className="form-label" htmlFor="inverter-provider">
          Inverter type
        </label>
        <select
          className="form-select"
          disabled={!canEdit || working}
          id="inverter-provider"
          onChange={event => onProviderChange(event.target.value as SolarMonitoringProvider)}
          value={selectedProvider}
        >
          {availableProviders.map(availableProvider => (
            <option key={availableProvider} value={availableProvider}>
              {providerLabel(availableProvider)}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="inverter-system-id">
          Site / System ID
        </label>
        <input
          autoComplete="off"
          className="form-control"
          disabled={!canEdit || working}
          id="inverter-system-id"
          onChange={event => onExternalSystemIdChange(event.target.value)}
          placeholder="Enphase site ID or SolarEdge system ID"
          value={externalSystemId}
        />
      </div>
      {message && <InverterModalStatus message={message} />}
      <div className="d-flex flex-column gap-2">
        <button
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={onSaveAndCheck}
          type="button"
        >
          {workingAction === 'save'
            ? <Spinner />
            : isProductionCheck
              ? 'Check Production'
              : 'Save and Check Connectivity'}
        </button>
        {isLocalDevelopment && (
          <button
            className="btn btn-outline-secondary"
            disabled={!canSubmit}
            onClick={onSimulate}
            type="button"
          >
            {workingAction === 'simulate'
              ? <Spinner />
              : isProductionCheck
                ? 'Simulate Successful Production'
                : 'Simulate Successful Connectivity'}
          </button>
        )}
        {canClear && (
          <button
            className="btn btn-outline-secondary"
            disabled={!canEdit || working}
            onClick={onClear}
            type="button"
          >
            {workingAction === 'clear' ? <Spinner /> : 'Clear monitoring details'}
          </button>
        )}
      </div>
    </Modal>,
    document.body,
  )
}

const ChecklistItemSolarMonitoring = ({
  item,
  project,
  onClick,
  isExpanded,
  onProjectChange,
}: ChecklistItemSolarMonitoringProps) => {
  const [provider, setProvider] = useState<SolarMonitoringProvider>(SolarMonitoringProvider.Enphase)
  const [externalSystemId, setExternalSystemId] = useState('')
  const [monitoringDetails, setMonitoringDetails] = useState<SolarMonitoringDetails | null>(null)
  const [workingAction, setWorkingAction] = useState<WorkingAction | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [modalMessage, setModalMessage] = useState<ModalMessage | null>(null)
  const isLocalDevelopment = process.env.NODE_ENV !== 'production'
  const checkKind = LoanFundingPhaseShared.getSolarMonitoringCheckKind(item)
  const isProductionCheck = checkKind === SolarMonitoringCheckKind.Production
  const availableProviders = [
    SolarMonitoringProvider.Enphase,
    SolarMonitoringProvider.SolarEdge,
  ]
  const selectedProvider = provider
  const assigned = Boolean(project.monitoringSiteId)
  const isVerified = isSolarMonitoringCheckComplete(checkKind, monitoringDetails?.evidence)
  const statusLabel = getSolarMonitoringStatusLabel(
    checkKind,
    monitoringDetails?.provider,
    monitoringDetails?.externalSystemId,
    monitoringDetails?.evidence,
  )
  const hasRequestedSeventyPercent = Boolean(project.fundReleasesRequested?.some(request => (
    request.type === FundReleaseType.InitialFundingRelease
  )))
  const canEdit = !hasRequestedSeventyPercent && !isProductionCheck
  const canClear = canEdit && assigned
  const working = workingAction !== null

  useEffect(() => {
    let isCurrent = true

    if (!project.monitoringSiteId) {
      setMonitoringDetails(null)
      setExternalSystemId('')
      return () => {
        isCurrent = false
      }
    }

    const loadMonitoringDetails = async (): Promise<void> => {
      try {
        const details = await api.get(
          `/solar-monitoring/loanapps/${project.id}/monitoring`,
        ) as SolarMonitoringDetails | null
        if (!isCurrent) return

        setMonitoringDetails(details)
        if (details) {
          setProvider(details.provider)
          setExternalSystemId(details.externalSystemId)
        }
      } catch (error) {
        console.error('Failed to load monitoring site details', errorToString(error))
        if (isCurrent) setMonitoringDetails(null)
      }
    }

    void loadMonitoringDetails()
    return () => {
      isCurrent = false
    }
  }, [project.id, project.monitoringSiteId])

  if (project.fundingSchedule !== FundingSchedule.SeventyThirty) {
    return null
  }

  const updateProject = (response: Record<string, unknown>) => {
    onProjectChange?.(new StatusData(response))
  }

  const saveInverterDetails = async (simulateSuccess = false): Promise<boolean> => {
    const trimmedSystemId = externalSystemId.trim()
    if (!selectedProvider || !trimmedSystemId) {
      toast.error('Select an inverter provider and enter the site/system ID')
      return false
    }

    setModalMessage({ text: 'Saving inverter details...', variant: 'muted' })
    const linkPath = simulateSuccess
      ? `/solar-monitoring/loanapps/${project.id}/monitoring/dev/link`
      : `/solar-monitoring/loanapps/${project.id}/monitoring/link`
    const response = await api.post(linkPath, {
      provider: selectedProvider,
      externalSystemId: trimmedSystemId,
    })
    setMonitoringDetails({
      monitoringSiteId: response.monitoringSiteId,
      provider: selectedProvider,
      externalSystemId: trimmedSystemId,
    })
    updateProject(response)
    setModalMessage({ text: 'Inverter details saved.', variant: 'success' })
    return true
  }

  const checkConnectivity = async (simulateSuccess = false): Promise<void> => {
    setModalMessage({
      text: isProductionCheck ? 'Checking production...' : 'Checking connectivity...',
      variant: 'muted',
    })
    const check = await api.post(
      `/solar-monitoring/loanapps/${project.id}/monitoring/check`,
      simulateSuccess
        ? {
          scenario: isProductionCheck ? 'three_day_production' : 'connectivity_only',
        }
        : {},
    )
    setMonitoringDetails(current => current
      ? { ...current, evidence: check.evidence as SolarMonitoringEvidence }
      : current)
    updateProject(check.project)
    const evidence = check.evidence as SolarMonitoringEvidence | undefined
    const complete = isSolarMonitoringCheckComplete(checkKind, evidence)
    const resultLabel = getSolarMonitoringStatusLabel(
      checkKind,
      selectedProvider,
      externalSystemId.trim(),
      evidence,
    )
    setModalMessage({
      text: resultLabel,
      variant: complete ? 'success' : 'warning',
    })
    if (complete) {
      toast.success(isProductionCheck
        ? 'Three consecutive qualifying production days detected.'
        : 'Connectivity verified.')
      setShowModal(false)
    }
  }

  const handleSaveAndCheck = async (): Promise<void> => {
    setWorkingAction('save')
    try {
      if (canEdit && !await saveInverterDetails()) {
        return
      }
      await checkConnectivity()
    } catch (error) {
      if (isMonitoringCheckCooldownError(error)) {
        setModalMessage({
          text: 'Monitoring data can be checked once every 3 hours. Please try again later.',
          variant: 'warning',
        })
        return
      }
      if (isMonitoringGrantUnavailableError(error)) {
        setModalMessage(null)
        toast.error(
          'Monitoring could not be linked. The homeowner may not have authorized this site, '
          + 'or the site may already be linked to another project.',
        )
        return
      }
      setModalMessage({
        text: isProductionCheck
          ? 'Unable to verify production data.'
          : 'Unable to verify connectivity.',
        variant: 'warning',
      })
    } finally {
      setWorkingAction(null)
    }
  }

  const handleSimulate = async (): Promise<void> => {
    setWorkingAction('simulate')
    try {
      if (!await saveInverterDetails(true)) {
        return
      }
      await checkConnectivity(true)
    } catch (error) {
      setModalMessage(null)
      toast.error(errorToString(error))
    } finally {
      setWorkingAction(null)
    }
  }

  const clearInverter = async (): Promise<void> => {
    setWorkingAction('clear')
    try {
      const response = await api.post(`/solar-monitoring/loanapps/${project.id}/monitoring/unlink`, {})
      updateProject(response)
      setMonitoringDetails(null)
      setExternalSystemId('')
      setShowModal(false)
      toast.success('Monitoring details cleared.')
    } catch (error) {
      toast.error(errorToString(error))
    } finally {
      setWorkingAction(null)
    }
  }

  return (
    <>
      <tr onClick={onClick} className="align-middle">
        <td style={{ width: '30px' }}>
          <CheckboxIcon isCompleted={isVerified} checklistOwner={ChecklistOwner.Installer} />
        </td>
        <td style={{ maxWidth: '420px' }}>
          <div className="fw-bold mb-1 pointer">{item.label}</div>
          <div className={`text-muted small ${isExpanded ? '' : 'd-block text-truncate'}`}>
            <b>Status:</b> {statusLabel}
          </div>
          {monitoringDetails ? <div>
            <div className="small text-muted mt-1">
              <b>Inverter Type:</b> {monitoringDetails.provider}
            </div>
            <div className="small text-muted mt-1">
              <b>Site/System ID:</b> {monitoringDetails.externalSystemId}
            </div>
          </div> : null}
        </td>
        <td className="text-center">
          <ManageSiteDetailsButton
            disabled={working}
            onClick={() => {
              setModalMessage(null)
              setShowModal(true)
            }}
          />
        </td>
        <td className="text-center">
          <span className="text-capitalize badge rounded-pill">
            {isVerified ? 'completed' : 'needs action'}
          </span>
        </td>
        <td className="text-capitalize text-center">{item.owner || 'installer'}</td>
      </tr>
      {showModal && (
        <InverterConnectivityModal
          availableProviders={availableProviders.length ? availableProviders : [selectedProvider]}
          canClear={canClear}
          canEdit={canEdit}
          checkKind={checkKind}
          externalSystemId={externalSystemId}
          isLocalDevelopment={isLocalDevelopment}
          message={modalMessage}
          onClear={() => { void clearInverter() }}
          onClose={() => setShowModal(false)}
          onExternalSystemIdChange={setExternalSystemId}
          onProviderChange={setProvider}
          onSaveAndCheck={() => { void handleSaveAndCheck() }}
          onSimulate={() => { void handleSimulate() }}
          selectedProvider={selectedProvider}
          workingAction={workingAction}
        />
      )}
    </>
  )
}

export default ChecklistItemSolarMonitoring
